import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import SaveIcon from '@mui/icons-material/Save';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  createWhatsAppTemplate,
  CreateWhatsAppTemplateResponse,
  updateWhatsAppTemplate,
  WhatsAppTemplate,
} from '../services/api';
import WhatsAppPreview from './WhatsAppPreview';

interface WhatsAppFormProps {
  onBack: () => void;
  onCreated?: (template: CreateWhatsAppTemplateResponse) => void;
  /** When provided, the form switches to edit mode. */
  editTemplate?: WhatsAppTemplate | null;
  onUpdated?: (template: WhatsAppTemplate) => void;
}

type Category = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

const CATEGORIES: Array<{ value: Category; label: string }> = [
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
];

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB — matches backend + Meta limits.
const ACCEPTED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export default function WhatsAppForm({
  onBack,
  onCreated,
  editTemplate = null,
  onUpdated,
}: WhatsAppFormProps) {
  const isEdit = !!editTemplate;
  const [message, setMessage] = useState(editTemplate?.message ?? '');
  const [purpose, setPurpose] = useState(editTemplate?.name ?? '');
  const [category, setCategory] = useState<Category>(() => {
    const raw = (editTemplate?.category || '').toString().trim().toUpperCase();
    return (['MARKETING', 'UTILITY', 'AUTHENTICATION'] as const).includes(
      raw as Category,
    )
      ? (raw as Category)
      : 'MARKETING';
  });

  // Image upload is available whenever we're editing a template. Templates
  // created in this form are body-only (we cannot register an IMAGE header
  // without `WHATSAPP_APP_ID`), but imported templates may have an IMAGE
  // header at Meta even when our DB hasn't captured a runtime image URL yet
  // (Meta's `header_handle` from import is not a stable public CDN URL, so
  // we never persist it). Always letting admins upload an image lets them
  // attach a Firebase-hosted runtime URL that broadcasts can use as
  // `image.link`, avoiding Meta's "media upload error" on send.
  const showImageUpload = isEdit;
  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<CreateWhatsAppTemplateResponse | null>(null);
  const [updatedSuccess, setUpdatedSuccess] = useState<WhatsAppTemplate | null>(null);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (message.trim().length === 0) return false;
    if (!isEdit && purpose.trim().length === 0) return false;
    return true;
  }, [message, purpose, submitting, isEdit]);

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    setError(null);
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      if (!ACCEPTED_IMAGE_MIME.includes(file.type)) {
        throw new Error('Unsupported image type. Use JPG, PNG, or WebP.');
      }
      if (file.size > MAX_IMAGE_BYTES) {
        throw new Error('Image is too large (max 5 MB).');
      }
      const dataUrl = await readFileAsDataUrl(file);
      setImageFileName(file.name);
      setImageDataUrl(dataUrl);
    } catch (e) {
      setImageError(e instanceof Error ? e.message : 'Failed to read image.');
      setImageFileName(null);
      setImageDataUrl(null);
    } finally {
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleClearImage = () => {
    setImageFileName(null);
    setImageDataUrl(null);
    setImageError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSuccess(null);
    setUpdatedSuccess(null);
    setSubmitting(true);
    try {
      if (isEdit && editTemplate) {
        const response = await updateWhatsAppTemplate(editTemplate.id, {
          message: message.trim(),
          category,
          ...(imageDataUrl ? { imageDataUrl } : {}),
        });
        setUpdatedSuccess(response);
        onUpdated?.(response);
      } else {
        const response = await createWhatsAppTemplate({
          message: message.trim(),
          purpose: purpose.trim(),
          category,
        });
        setSuccess(response);
        onCreated?.(response);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isEdit
          ? 'Failed to update WhatsApp template.'
          : 'Failed to create WhatsApp template.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      <Button
        variant="text"
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to templates
      </Button>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366', fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {isEdit ? 'Edit WhatsApp template' : 'Create WhatsApp template'}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
        {isEdit
          ? 'Edit the header, body or category. Edited templates are re-submitted to Meta for approval.'
          : 'Submit a template for approval. Once approved, you can broadcast it to recipients from the templates list.'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 380px' },
          alignItems: 'flex-start',
        }}
      >
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Template name"
              required
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              helperText={
                isEdit
                  ? 'Template name cannot be changed after creation.'
                  : 'Used as the WhatsApp template name. Letters, numbers and underscores only.'
              }
              inputProps={{ maxLength: 120 }}
              disabled={isEdit}
              fullWidth
            />

            <FormControl fullWidth disabled={isEdit}>
              <InputLabel id="whatsapp-category-label">Category</InputLabel>
              <Select
                labelId="whatsapp-category-label"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value as Category)}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
              {isEdit && (
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Category cannot be changed after the template is created.
                </Typography>
              )}
            </FormControl>

            {showImageUpload && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Header image (optional)
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
                >
                  JPG, PNG or WebP, up to 5 MB. Upload a new file only if you
                  want to replace the existing header image.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'center' }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<ImageIcon />}
                    sx={{ textTransform: 'none' }}
                    disabled={submitting}
                  >
                    {imageDataUrl ? 'Replace image' : 'Upload new image'}
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept={ACCEPTED_IMAGE_MIME.join(',')}
                      hidden
                      onChange={handleImageChange}
                    />
                  </Button>
                  {imageFileName && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        <strong>{imageFileName}</strong>
                      </Typography>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={handleClearImage}
                        sx={{ textTransform: 'none' }}
                        disabled={submitting}
                      >
                        Remove
                      </Button>
                    </Stack>
                  )}
                </Stack>
                {imageError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    {imageError}
                  </Alert>
                )}
                {editTemplate?.hasImg && !editTemplate?.imageUrl && !imageDataUrl && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                    This template has an image header on Meta but no image
                    has been uploaded yet. Upload one here before broadcasting,
                    otherwise sends will be rejected.
                  </Alert>
                )}
                {editTemplate?.imageUrl && !imageDataUrl && (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    Existing header image is shown in the preview. Upload a
                    new file to replace it.
                  </Alert>
                )}
              </Box>
            )}

            <TextField
              label="Message content"
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={5}
              inputProps={{ maxLength: 1024 }}
              helperText={`${message.length}/1024`}
              fullWidth
            />

            {error && <Alert severity="error">{error}</Alert>}
            {updatedSuccess && (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small" onClick={onBack}>
                    Go to templates
                  </Button>
                }
              >
                Template <strong>{updatedSuccess.name}</strong> updated (status:{' '}
                <strong>{updatedSuccess.status}</strong>). Edits are re-reviewed by Meta.
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small" onClick={onBack}>
                    Go to templates
                  </Button>
                }
              >
                Template <strong>{success.name}</strong> submitted for approval (status:{' '}
                <strong>{success.status}</strong>). You can send it to recipients from
                the templates list once it is approved.
              </Alert>
            )}

            {isEdit && (
              <Alert severity="info" variant="outlined">
                WhatsApp templates can only be edited once every 24 hours.
              </Alert>
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button type="button" color="inherit" onClick={onBack} disabled={submitting}>
                Cancel
              </Button>
              {/*
                Hide the primary action once the create/update succeeded so the
                admin can't accidentally re-submit the same form. The success
                Alert above provides the "Go to templates" affordance.
              */}
              {!(success || updatedSuccess) && (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!canSubmit}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                    ) : isEdit ? (
                      <SaveIcon />
                    ) : (
                      <AddIcon />
                    )
                  }
                  sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }}
                >
                  {submitting
                    ? isEdit
                      ? 'Saving…'
                      : 'Creating…'
                    : isEdit
                    ? 'Save changes'
                    : 'Create'}
                </Button>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Live preview
        </Typography>
        <WhatsAppPreview
          body={message}
          imageUrl={
            showImageUpload
              ? imageDataUrl ?? editTemplate?.imageUrl ?? undefined
              : undefined
          }
        />
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
          {'Use *bold* for bold text. {{1}}, {{2}} … will become variables.'}
        </Typography>
      </Box>
      </Box>
    </Box>
  );
}
